import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { keys } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { parseMeterValue } from '@blimo/shared';
import {
  applyProblemToForm,
  Button,
  CheckboxField,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormRootError,
  InputField,
  toast,
} from '@blimo/ui';
import { currentYear } from './utils.js';

const schema = z.object({
  year: z
    .string()
    .trim()
    .regex(/^\d{4}$/, 'Bitte ein Jahr (JJJJ) angeben.')
    .refine((v) => Number(v) >= 2000 && Number(v) <= 2100, 'Jahr zwischen 2000 und 2100.'),
  value: z
    .string()
    .trim()
    .refine((v) => v === '' || parseMeterValue(v) !== null, 'Nur Ziffern und höchstens ein Komma.'),
  read_at: z.string(),
  is_new_meter: z.boolean(),
});

/**
 * FI-903a: einzelnen Zählerstand für diesen Zähler und ein beliebiges Jahr anlegen (`meter-readings.manage`).
 * Ohne Wert entsteht eine offene Ablesung; Dubletten (Zähler/Jahr) lehnt der Server mit 409 ab.
 * @param {{ meter: { id: number }, open: boolean, onOpenChange: (open: boolean) => void }} props
 */
export function AddReadingDialog({ meter, open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>{open && <AddReadingForm meter={meter} onDone={() => onOpenChange(false)} />}</DialogContent>
    </Dialog>
  );
}

function AddReadingForm({ meter, onDone }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: { year: String(currentYear()), value: '', read_at: '', is_new_meter: false },
  });

  const submit = form.handleSubmit(async (values) => {
    try {
      await api.admin.meterReadings.create(meter.id, {
        year: Number(values.year),
        value: values.value ? parseMeterValue(values.value) : null,
        read_at: values.read_at || null,
        is_new_meter: values.is_new_meter,
      });
      await queryClient.invalidateQueries({ queryKey: keys.admin.detail('meters', meter.id) });
      queryClient.invalidateQueries({ queryKey: keys.admin.resource('meter-readings') });
      toast.success(`Zählerstand ${values.year} angelegt`);
      onDone();
    } catch (error) {
      applyProblemToForm(error, form.setError, { fields: ['year', 'value', 'read_at', 'is_new_meter'] });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        <DialogHeader>
          <DialogTitle>Zählerstand hinzufügen</DialogTitle>
          <DialogDescription>Einzelner Wert für diesen Zähler in einem beliebigen Jahr. Ohne Wert wird eine offene Ablesung angelegt.</DialogDescription>
        </DialogHeader>
        <FormRootError />
        <InputField name="year" label="Jahr" inputMode="numeric" maxLength={4} className="w-28" autoComplete="off" />
        <InputField name="value" label="Zählerstand" inputMode="decimal" autoComplete="off" description="Optional, z. B. 12345,6" />
        <InputField name="read_at" label="Abgelesen am" type="date" description="Optional, Standard: heute" />
        <CheckboxField name="is_new_meter" label="Neuer Zähler (Zählertausch)" />
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onDone}>
            Abbrechen
          </Button>
          <Button type="submit" loading={form.formState.isSubmitting}>
            Hinzufügen
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
